import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PbiReportDefinitionViewModel } from '@rsmus/ecp-userservice'
import { Styles } from '../../types'
import { tokens } from '../../styles/materialTheme'
import SortableHeader from '../../rsmCoreComponents/components/SortableHeader/SortableHeader'
import OverflowTooltip from '../OverFlowToolTip/OverFlowToolTip'
import ActionMenu, { ActionMenuItem } from '../Invoicing/Invoices/ActionMenu'
import { PBI_REPORT_COLUMNS } from '../../utils/constants/constants'

const styles: Styles = {
  reportsTable: (theme) => ({
    '&.MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
      overflowX: 'hidden',

      '& .MuiTableCell-root': {
        borderBottom: 'none',
        maxWidth: '11.25rem',
      },
      '& .MuiTableCell-head': {
        padding: '0.75rem',
        fontFamily: 'Prelo-Bold, sans-serif',
        fontSize: '0.875rem',
        lineHeight: 1.15,
        textTransform: 'uppercase',
        verticalAlign: 'middle',
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-word',
      },

      '& td.MuiTableCell-root': {
        fontFamily: 'Prelo-Book, sans-serif',
      },
      '& tbody .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: tokens.colors.rsmGray.accessibility,
      },
      '& .MuiTableCell-body': {
        padding: '1rem',
        fontSize: '0.95rem',
        wordBreak: 'break-all',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      // Action column CSS
      '& thead th.actions, & tbody td.actions': {
        width: '3.5rem',
        minWidth: '3.5rem',
        maxWidth: '3.5rem',
        textAlign: 'right',
        paddingRight: '0.5rem',
        paddingLeft: '0.5rem',
        alignItems: 'right',
      },
    },
    '.sortable-header': {
      cursor: 'pointer',
    },
    // Hide columns on tablet breakpoint for better responsiveness
    // New PBIReportDefinition schema column order:
    // 1=ReportName, 2=ServicePrincipalId, 3=IsClientLevelAuthorization,
    // 4=CEMAppId, 5=TenantId, 6=ClientSecretName, 7=Actions
    // Hide TenantId (5) and ClientSecretName (6) on tablet for space
    [theme.breakpoints.down('tablet')]: {
      '& .MuiTableCell-root:nth-of-type(5), & .MuiTableCell-root:nth-of-type(6)':
        {
          display: 'none',
        },
    },
  }),
  date: { textTransform: 'uppercase' },
}

/**
 * Props for the PbiReportsTable component.
 * Displays a sortable table of PBI Report Definitions for admin management.
 */
interface PbiReportsTableProps {
  /** Report data containing array of report definitions and total count for pagination */
  ReportsData: { data: PbiReportDefinitionViewModel[]; totalRows: number }
  /** Callback when user clicks a column header to sort */
  onSort: (columnName: string, direction: 'asc' | 'desc') => void
  /** Currently active sort column name */
  sortColumn: string
  /** Current sort direction */
  sortDirection: 'asc' | 'desc'
  /** Flag indicating if the component is mounted (used for animations) */
  isMounted: boolean
  /** Optional callback when user selects Edit action for a report */
  onEditReport?: (report: PbiReportDefinitionViewModel) => void
  /** Optional callback when user selects Remove action for a report */
  onRemoveReport?: (report: PbiReportDefinitionViewModel) => void
}

const PbiReportsTable = ({
  ReportsData,
  onSort,
  sortColumn,
  sortDirection,
  isMounted,
  onEditReport,
  onRemoveReport,
}: PbiReportsTableProps) => {
  const { t } = useTranslation()

  const handleSort = (columnName: string) => {
    const newSortDirection: 'asc' | 'desc' =
      sortColumn === columnName && sortDirection === 'asc' ? 'desc' : 'asc'
    onSort(columnName, newSortDirection)
  }

  const getSortableHeader = (backendKey: string, displayValue: string) => (
    <TableCell>
      <SortableHeader
        columnName={backendKey}
        currentSortColumn={sortColumn || ''}
        sortDirection={sortDirection || ''}
        isMounted={isMounted}
        handleSort={handleSort}>
        {displayValue}
      </SortableHeader>
    </TableCell>
  )

  /**
   * Generates the action menu items for a report definition row.
   * Includes Edit and Remove actions that trigger the respective callbacks.
   */
  const generateMenuItems = useCallback(
    (
      ReportData: PbiReportDefinitionViewModel,
    ): ActionMenuItem<PbiReportDefinitionViewModel>[] => [
      {
        label: t('PbiReport.Columns.Edit'),
        handleAction: () => onEditReport?.(ReportData),
      },
      {
        label: t('PbiReport.Columns.Remove'),
        handleAction: () => onRemoveReport?.(ReportData),
      },
    ],
    [onEditReport, onRemoveReport],
  )

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table
          id="pbiReportsTable"
          sx={styles.reportsTable}
          aria-label={t('PbiReport.Title')}>
          <Box component="caption" sx={visuallyHidden}>
            {t('PbiReport.Title')}
          </Box>
          <TableHead>
            <TableRow>
              {/* Column 1: Report Short Name - unique identifier for the report definition */}
              {getSortableHeader(
                PBI_REPORT_COLUMNS.REPORT_NAME,
                t('PbiReport.Columns.ReportName'),
              )}
              {/* Column 2: Service Principal ID (renamed from ClientID in new schema) */}
              {getSortableHeader(
                PBI_REPORT_COLUMNS.SERVICE_PRINCIPAL_ID,
                t('PbiReport.Columns.ServicePrincipalID'),
              )}
              {/* Column 3: Is Client-Level Authorization - flag for auth model type */}
              {getSortableHeader(
                PBI_REPORT_COLUMNS.IS_CLIENT_LEVEL_AUTHORIZATION,
                t('PbiReport.Columns.IsClientLevelAuthorization'),
              )}
              {/* Column 4: CEM Application ID */}
              {getSortableHeader(
                PBI_REPORT_COLUMNS.CEM_APP_ID,
                t('PbiReport.Columns.CEMAppID'),
              )}
              {/* Column 5: Tenant ID - hidden on tablet breakpoint */}
              {getSortableHeader(
                PBI_REPORT_COLUMNS.TENANT_ID,
                t('PbiReport.Columns.TenantID'),
              )}
              {/* Column 6: Client Secret Name - hidden on tablet breakpoint */}
              {getSortableHeader(
                PBI_REPORT_COLUMNS.CLIENT_SECRET_NAME,
                t('PbiReport.Columns.ClientSecretName'),
              )}
              {/* Column 7: Actions menu */}
              <TableCell className="actions">
                <Box component="span" sx={visuallyHidden}>
                  {t('Common.Actions')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ReportsData?.data.map((ReportData, index) => (
              <TableRow
                key={`report-${ReportData.id}`}
                data-testid={`ReportsData_Row_${index}`}>
                {/* Cell 1: Report Short Name */}
                <TableCell data-testid={`reportName_Cell_${index}`}>
                  <OverflowTooltip id={`reportName-tooltip-${index}`}>
                    {ReportData.reportName}
                  </OverflowTooltip>
                </TableCell>
                {/* Cell 2: Service Principal ID (uses clientId property from API response) */}
                <TableCell data-testid={`servicePrincipalId_Cell_${index}`}>
                  <OverflowTooltip id={`servicePrincipalId-tooltip-${index}`}>
                    {ReportData.servicePrincipalId}
                  </OverflowTooltip>
                </TableCell>
                {/* Cell 3: Is Client-Level Authorization (boolean rendered as Yes/No) */}
                <TableCell
                  data-testid={`isClientLevelAuthorization_Cell_${index}`}>
                  {ReportData.isClientLevelAuthorization
                    ? t('PowerBI.ReportModal.Yes')
                    : t('PowerBI.ReportModal.No')}
                </TableCell>
                {/* Cell 4: CEM Application ID */}
                <TableCell data-testid={`appId_Cell_${index}`}>
                  <Box component="span" sx={styles.date}>
                    <OverflowTooltip id={`appId-tooltip-${index}`}>
                      {ReportData.cemAppId}
                    </OverflowTooltip>
                  </Box>
                </TableCell>
                {/* Cell 5: Tenant ID - hidden on tablet */}
                <TableCell data-testid={`tenantId_Cell_${index}`}>
                  <OverflowTooltip id={`tenantId-tooltip-${index}`}>
                    {ReportData.tenantId}
                  </OverflowTooltip>
                </TableCell>
                {/* Cell 6: Client Secret Name - hidden on tablet */}
                <TableCell data-testid={`secretName_Cell_${index}`}>
                  <OverflowTooltip id={`secretName-tooltip-${index}`}>
                    {ReportData.clientSecretName}
                  </OverflowTooltip>
                </TableCell>
                {/* Cell 7: Actions menu */}
                <TableCell className="actions">
                  <ActionMenu
                    actionButtonAriaControls={`report-${ReportData.id}-menu`}
                    actionButtonDescribedBy={ReportData.id.toString()}
                    id={ReportData.id.toString()}
                    data={ReportData}
                    dataTestId={`Btn_Ellipses_${index}`}
                    errorHeader={t('PbiReport.Errors.ActionError')}
                    errorMessage={t('PbiReport.Errors.ActionErrorMessage')}
                    errorMessageForNotFound={t(
                      'PbiReport.Errors.ActionErrorNotFound',
                    )}
                    menuItems={generateMenuItems(ReportData)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default PbiReportsTable
